import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { BookOpen, Heart, Sparkles, Check, Star, LogOut, Menu, X, Phone, Youtube, Facebook, Building2, Smartphone, Video, Lock } from "lucide-react";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LandingPage = () => {
  const navigate = useNavigate();
  const { logout, user, getAuthHeaders } = useAuth();
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [hasAccess, setHasAccess] = useState(false);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const response = await axios.get(`${API}/content`);
        setContent(response.data);
      } catch (error) {
        console.error("Erreur lors du chargement du contenu", error);
      } finally {
        setLoading(false);
      }
    };

    const checkAccess = async () => {
      try {
        await axios.get(`${API}/book/content`, {
          headers: getAuthHeaders()
        });
        setHasAccess(true);
      } catch (error) {
        setHasAccess(false);
      }
    };

    fetchContent();
    checkAccess();
  }, [getAuthHeaders]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]">
        <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-[#F97316]"></div>
      </div>
    );
  }

  const benefits = [
    {
      icon: <BookOpen className="w-8 h-8" />,
      color: "bg-orange-100 text-orange-600",
      title: content?.benefit_1_title || "Comprendre le sens profond de la croix",
      description: content?.benefit_1_description || "Découvrez la signification biblique."
    },
    {
      icon: <Heart className="w-8 h-8" />,
      color: "bg-blue-100 text-blue-600",
      title: content?.benefit_2_title || "Trouver une espérance inébranlable",
      description: content?.benefit_2_description || "Ancrez votre foi."
    },
    {
      icon: <Sparkles className="w-8 h-8" />,
      color: "bg-purple-100 text-purple-600",
      title: content?.benefit_3_title || "Vivre libre de tout esclavage spirituel",
      description: content?.benefit_3_description || "Expérimentez la vraie liberté."
    }
  ];

  return (
    <div className="min-h-screen bg-[#FAFAFA]" style={{ fontFamily: 'Manrope, sans-serif' }}>
      {/* Header with Glassmorphism */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center">
                <BookOpen className="w-6 h-6 text-white" />
              </div>
              <span className="text-xl font-bold text-[#1E3A8A]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Le Mystère
              </span>
            </div>

            {/* Desktop Menu */}
            <div className="hidden md:flex items-center gap-4">
              <div className="text-sm text-[#475569]">
                <Phone className="inline w-4 h-4 mr-1" />
                {user?.phone}
              </div>
              <Button
                variant="ghost"
                onClick={logout}
                className="text-[#475569] hover:text-[#F97316]"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Déconnexion
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button 
              className="md:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X /> : <Menu />}
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden mt-4 pb-4 border-t pt-4">
              <div className="text-sm text-[#475569] mb-3">
                Connecté: {user?.phone}
              </div>
              <Button
                variant="ghost"
                onClick={logout}
                className="w-full justify-start"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Déconnexion
              </Button>
            </div>
          )}
        </div>
      </header>

      {/* Hero Section - Asymmetric */}
      <section className="py-20 md:py-32 px-6 md:px-12 max-w-7xl mx-auto bg-gray-900">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left: Text Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            className="space-y-6"
          >
            <div className="inline-block">
              <span className="text-sm font-semibold text-[#F97316] uppercase tracking-wider bg-orange-900/30 px-4 py-2 rounded-full border border-orange-500">
                📖 Ebook Chrétien
              </span>
            </div>

            {/* Photo du pasteur */}
            {content?.hero_author_photo_url && (
              <div className="flex items-center gap-4 my-4">
                <img 
                  src={content.hero_author_photo_url} 
                  alt={content.hero_author}
                  className="w-16 h-16 rounded-full object-cover border-4 border-orange-500"
                />
                <div>
                  <p className="text-sm font-semibold text-gray-300 uppercase tracking-wider">
                    {content?.hero_author || "Pasteur Joseph Kalala"}
                  </p>
                </div>
              </div>
            )}
            
            <h1 
              className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-tight"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              {content?.hero_title || "Pasteur Joseph Kalala - Le Mystère de la Croix"}
            </h1>

            {!content?.hero_author_photo_url && (
              <p className="text-sm font-semibold text-gray-300 uppercase tracking-wider">
                {content?.hero_author || "Pasteur Joseph Kalala"}
              </p>
            )}

            <p className="text-lg text-gray-300 leading-relaxed">
              {content?.hero_subtitle || "L'espérance qui change tout"}
            </p>

            <p className="text-base text-gray-200 leading-relaxed">
              {content?.hero_description || "Découvre comment la mort de Jésus brise tout asservissement."}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              {hasAccess ? (
                <Button
                  onClick={() => navigate("/read")}
                  className="bg-green-600 hover:bg-green-700 text-white font-bold py-6 px-10 rounded-full shadow-[0_8px_20px_rgba(34,197,94,0.25)] transition-all hover:-translate-y-1 active:translate-y-0 text-lg"
                >
                  📖 Lire le livre maintenant
                </Button>
              ) : (
                <Button
                  onClick={() => navigate("/checkout")}
                  className="bg-[#F97316] hover:bg-[#EA580C] text-white font-bold py-6 px-10 rounded-full shadow-[0_8px_20px_rgba(249,115,22,0.25)] transition-all hover:-translate-y-1 active:translate-y-0 text-lg"
                  data-testid="buy-hero-button"
                >
                  Acheter maintenant - {content?.price_amount || "5"} {content?.price_currency || "USD"}
                </Button>
              )}
            </div>

            {/* Payment Methods */}
            <div className="flex flex-wrap gap-3 items-center pt-4">
              <span className="text-sm text-gray-300 font-semibold">Paiement sécurisé :</span>
              <div className="flex gap-2">
                <span className="bg-[#22C55E] text-white text-xs font-bold px-3 py-1.5 rounded-lg">M-Pesa</span>
                <span className="bg-[#EF4444] text-white text-xs font-bold px-3 py-1.5 rounded-lg">Airtel Money</span>
                <span className="bg-[#F97316] text-white text-xs font-bold px-3 py-1.5 rounded-lg">Orange Money</span>
              </div>
            </div>

            {/* Social Proof - Purchase Counter */}
            <div className="mt-6 bg-green-900/30 border-2 border-green-500 rounded-lg p-4 inline-block">
              <p className="text-green-300 font-semibold flex items-center gap-2">
                <span className="text-2xl">✅</span>
                <span className="text-base">
                  <strong className="text-white text-lg">25 personnes</strong> ont déjà acheté ce livre!
                </span>
              </p>
            </div>
          </motion.div>

          {/* Right: Book Visual */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="relative"
          >
            <div className="relative rounded-3xl overflow-hidden shadow-[0_20px_60px_rgba(0,0,0,0.15)]">
              {content?.hero_image_url ? (
                <img 
                  src={content.hero_image_url} 
                  alt="Pasteur Joseph Kalala - Le Mystère de la Croix"
                  className="w-full h-auto"
                />
              ) : (
                <div className="aspect-[3/4] bg-gradient-to-br from-orange-500 via-purple-600 to-blue-600 flex items-center justify-center p-12">
                  <div className="text-center text-white">
                    <BookOpen className="w-24 h-24 mx-auto mb-6 opacity-90" />
                    <h3 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      Pasteur Joseph Kalala - Le Mystère de la Croix
                    </h3>
                    <p className="text-lg opacity-90">L'espérance qui change tout</p>
                  </div>
                </div>
              )}
            </div>

            {/* Floating Badge */}
            <div className="absolute -bottom-6 -right-6 bg-white rounded-2xl shadow-[0_10px_40px_rgba(0,0,0,0.1)] p-6">
              <div className="flex items-center gap-2 mb-2">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                ))}
              </div>
              <p className="text-sm font-semibold text-[#0F172A]">Transforme des vies</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Benefits Section - Bento Grid */}
      <section className="py-20 md:py-32 px-6 md:px-12 max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 
            className="text-3xl sm:text-4xl font-bold tracking-tight text-[#0F172A] mb-4"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            Ce que vous allez découvrir
          </h2>
          <p className="text-lg text-[#475569]">Des vérités qui transformeront votre vie spirituelle</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {benefits.map((benefit, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="bg-white rounded-3xl p-8 shadow-[0_4px_20px_rgba(0,0,0,0.04)] border border-gray-100 hover:shadow-[0_8px_30px_rgba(0,0,0,0.08)] transition-all"
            >
              <div className={`w-16 h-16 rounded-2xl ${benefit.color} flex items-center justify-center mb-6`}>
                {benefit.icon}
              </div>
              <h3 
                className="text-xl font-bold text-[#0F172A] mb-3"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                {benefit.title}
              </h3>
              <p className="text-base text-[#475569] leading-relaxed">
                {benefit.description}
              </p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Gallery Section - Aperçu du livre */}
      {content?.gallery_images && content.gallery_images.length > 0 && (
        <section className="py-20 md:py-32 px-6 md:px-12 max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 
              className="text-3xl sm:text-4xl font-bold tracking-tight text-[#0F172A] mb-4"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              📸 Aperçu du livre
            </h2>
            <p className="text-lg text-[#475569]">Découvrez quelques pages de "Pasteur Joseph Kalala - Le Mystère de la Croix"</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {content.gallery_images.map((imageUrl, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="relative group overflow-hidden rounded-2xl shadow-[0_4px_20px_rgba(0,0,0,0.08)] hover:shadow-[0_8px_30px_rgba(0,0,0,0.15)] transition-all"
              >
                <img 
                  src={imageUrl} 
                  alt={`Aperçu ${index + 1}`}
                  className="w-full h-64 object-cover transition-transform duration-300 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-4">
                  <p className="text-white text-sm font-semibold">Page {index + 1}</p>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-12">
            <p className="text-[#475569] mb-6">
              ✨ Et bien plus encore dans le livre complet !
            </p>
            {!hasAccess && (
              <Button
                onClick={() => navigate("/checkout")}
                className="bg-[#F97316] hover:bg-[#EA580C] text-white font-bold py-4 px-8 rounded-full shadow-lg"
              >
                Acheter maintenant - {content?.price_amount || "5"} {content?.price_currency || "USD"}
              </Button>
            )}
          </div>
        </section>
      )}

      {/* Videos Section */}
      <section className="py-20 md:py-32 px-6 md:px-12 max-w-7xl mx-auto bg-gradient-to-br from-purple-50 to-blue-50">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center"
        >
          <h2 
            className="text-3xl sm:text-4xl font-bold tracking-tight text-[#0F172A] mb-4"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            🎥 Enseignements Vidéo
          </h2>
          <p className="text-lg text-[#475569] mb-12 max-w-2xl mx-auto">
            Approfondissez votre compréhension avec nos enseignements vidéo exclusifs
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto mb-12">
            {/* Video preview cards */}
            {[1, 2, 3].map((num) => (
              <motion.div
                key={num}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: num * 0.1 }}
                className="relative group"
              >
                <div className="relative rounded-xl overflow-hidden shadow-lg">
                  <div className="aspect-video bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center">
                    <div className="text-white text-center">
                      <Video className="w-16 h-16 mx-auto mb-3 opacity-80" />
                      <p className="font-semibold">Vidéo {num}</p>
                    </div>
                  </div>
                  <div className="absolute inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center">
                    <div className="text-center">
                      <Lock className="w-12 h-12 text-white mx-auto mb-2" />
                      <p className="text-white font-semibold text-sm">Débloquer pour regarder</p>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="bg-white rounded-2xl p-8 max-w-2xl mx-auto shadow-xl">
            <div className="flex items-center justify-center gap-4 mb-6">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center">
                <Video className="w-8 h-8 text-purple-600" />
              </div>
              <div className="text-left">
                <p className="text-3xl font-bold text-purple-600">3 USD</p>
                <p className="text-sm text-gray-600">environ 6,000 CDF</p>
              </div>
            </div>

            <div className="space-y-3 text-left mb-6 max-w-md mx-auto">
              <div className="flex items-center gap-3">
                <Check className="w-5 h-5 text-green-600 flex-shrink-0" />
                <span className="text-gray-700">Accès illimité à toutes les vidéos</span>
              </div>
              <div className="flex items-center gap-3">
                <Check className="w-5 h-5 text-green-600 flex-shrink-0" />
                <span className="text-gray-700">Enseignements approfondis</span>
              </div>
              <div className="flex items-center gap-3">
                <Check className="w-5 h-5 text-green-600 flex-shrink-0" />
                <span className="text-gray-700">Accès immédiat après paiement</span>
              </div>
            </div>

            <Button
              onClick={() => navigate("/videos")}
              className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white font-bold py-4 px-8 rounded-full text-lg shadow-lg"
            >
              <Video className="mr-2 h-5 w-5" />
              Découvrir les vidéos
            </Button>
          </div>
        </motion.div>
      </section>

      {/* Testimonial Section */}
      <section className="py-20 md:py-32 px-6 md:px-12 bg-gray-900">
        <div className="max-w-4xl mx-auto text-center">
          <h2 
            className="text-3xl sm:text-4xl font-bold tracking-tight text-[#0F172A] mb-12"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            Témoignages
          </h2>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-white rounded-3xl p-10 shadow-[0_10px_40px_rgba(0,0,0,0.08)]"
          >
            <div className="flex justify-center mb-4">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-6 h-6 fill-yellow-400 text-yellow-400" />
              ))}
            </div>
            <p className="text-lg italic text-[#475569] leading-relaxed mb-6">
              "{content?.testimonial_text || "Ce livre a transformé ma compréhension de la croix."}"
            </p>
            <p className="text-sm font-semibold text-[#0F172A] uppercase tracking-wider">
              — {content?.testimonial_author || "Marie K., Kinshasa"}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 md:py-32 px-6 md:px-12 max-w-7xl mx-auto text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-3xl p-12 md:p-16 text-white"
        >
          <h2 
            className="text-3xl sm:text-4xl font-bold mb-6"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            Prêt à découvrir le mystère ?
          </h2>
          <p className="text-lg mb-8 opacity-90">
            Accédez immédiatement à l'ebook après votre paiement
          </p>
          <Button
            onClick={() => navigate("/checkout")}
            className="bg-white text-[#F97316] hover:bg-gray-100 font-bold py-6 px-10 rounded-full text-lg shadow-lg"
          >
            Acheter pour {content?.price_amount || "5"} {content?.price_currency || "USD"}
          </Button>
        </motion.div>
      </section>

      {/* Section Dons/Soutien */}
      <section className="py-20 px-6 md:px-12 max-w-7xl mx-auto bg-gradient-to-br from-blue-50 to-purple-50">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center"
        >
          <h2 
            className="text-3xl sm:text-4xl font-bold mb-4 text-[#1E3A8A]"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            💝 Soutenez Notre Livre
          </h2>
          <p className="text-lg text-[#475569] mb-10 max-w-2xl mx-auto">
            Votre générosité nous aide à continuer à partager la Parole de Dieu et à produire des contenus qui transforment des vies.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {/* Mobile Money Don */}
            <motion.div
              whileHover={{ scale: 1.02 }}
              className="bg-white rounded-2xl p-8 shadow-lg border-2 border-orange-200"
            >
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Smartphone className="w-8 h-8 text-orange-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Mobile Money</h3>
              <p className="text-sm text-gray-600 mb-6">
                Dons de tout montant via Orange Money, M-Pesa ou Airtel Money
              </p>
              <div className="space-y-2 text-sm text-gray-700 mb-6">
                <p>✓ Processus rapide et automatique</p>
                <p>✓ Montants à partir de 1,000 CDF</p>
                <p>✓ Reçu instantané</p>
              </div>
              <Button
                onClick={() => navigate("/checkout")}
                className="w-full bg-orange-500 hover:bg-orange-600 text-white py-3 rounded-full"
              >
                Faire un don maintenant
              </Button>
            </motion.div>

            {/* Virement Bancaire Don */}
            <motion.div
              whileHover={{ scale: 1.02 }}
              className="bg-white rounded-2xl p-8 shadow-lg border-2 border-blue-200"
            >
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Building2 className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Virement Bancaire</h3>
              <p className="text-sm text-gray-600 mb-4">
                Pour les dons généreux de 500 USD et plus
              </p>
              <div className="bg-blue-50 rounded-lg p-4 text-left space-y-2 text-sm mb-4">
                <div className="flex justify-between">
                  <span className="font-semibold text-gray-700">Banque:</span>
                  <span className="text-gray-900">{content?.bank_name || "Rawbank"}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-semibold text-gray-700">Compte:</span>
                  <span className="text-gray-900">{content?.bank_account_number || "01179253001"}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-semibold text-gray-700">Titulaire:</span>
                  <span className="text-gray-900">{content?.bank_account_holder || "Sarah Avundo"}</span>
                </div>
              </div>
              <p className="text-xs text-gray-600 mb-4">
                📞 Après le virement, contactez-nous au <strong>+243 808 408 407</strong>
              </p>
              <Button
                variant="outline"
                className="w-full border-2 border-blue-500 text-blue-600 hover:bg-blue-50 py-3 rounded-full"
                onClick={() => {
                  // Copy account number to clipboard
                  navigator.clipboard.writeText(content?.bank_account_number || "01179253001");
                  alert("✅ Numéro de compte copié!");
                }}
              >
                Copier le numéro de compte
              </Button>
            </motion.div>
          </div>

          <p className="text-sm text-gray-600 mt-8 italic">
            "Que celui qui reçoit l'enseignement de la parole fasse part de tous ses biens à celui qui l'enseigne." - Galates 6:6
          </p>
        </motion.div>
      </section>

      {/* Footer */}
      <footer className="bg-[#1E3A8A] text-white py-12 px-6">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-sm opacity-80">
            © 2024 Pasteur Joseph Kalala - Le Mystère de la Croix. Tous droits réservés.
          </p>
          <p className="text-sm opacity-70 mt-2">
            Développé par Daniel Mawodjo
          </p>
          
          {/* Social Media Links */}
          {(content?.youtube_url || content?.facebook_url) && (
            <div className="flex justify-center gap-4 mt-6 mb-4">
              {content?.youtube_url && (
                <a 
                  href={content.youtube_url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 bg-red-600 hover:bg-red-700 px-4 py-2 rounded-full transition-all hover:scale-105"
                >
                  <Youtube className="w-5 h-5" />
                  <span className="text-sm font-semibold">YouTube</span>
                </a>
              )}
              {content?.facebook_url && (
                <a 
                  href={content.facebook_url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded-full transition-all hover:scale-105"
                >
                  <Facebook className="w-5 h-5" />
                  <span className="text-sm font-semibold">Facebook</span>
                </a>
              )}
            </div>
          )}
          
          <div className="flex justify-center gap-6 mt-4">
            <button onClick={() => navigate("/privacy-policy")} className="text-sm hover:text-orange-400 transition">
              Confidentialité
            </button>
            <button onClick={() => navigate("/terms")} className="text-sm hover:text-orange-400 transition">
              Conditions
            </button>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
